import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Context erstellen
export const SettingsContext = createContext();

// Default-Werte für Settings
const DEFAULT_SETTINGS = {
  appName: 'Padel Turnier',
  logoUrl: '',
  showSponsor: true,
  themeMode: 'auto',
  slideDuration: 10,
  sponsorRotationDuration: 5,
  qrUrl: '',
  sponsors: []
};

/**
 * SettingsProvider - Globaler Context für Systemeinstellungen
 *
 * Stellt folgende Settings bereit:
 * - appName: Name der App (wird als Browser-Titel verwendet)
 * - logoUrl: URL des Haupt-Logos
 * - sponsorLogoUrl: URL des Sponsor-Logos
 * - showSponsor: Ob der Sponsor-Banner angezeigt wird
 * - themeMode: Theme-Modus (auto, force_light, force_dark)
 */
export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Settings vom Server laden
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsResponse, sponsorsResponse] = await Promise.all([
        axios.get('/api/system-settings'),
        axios.get('/api/sponsors').catch(() => ({ data: [] }))
      ]);
      const data = settingsResponse.data;

      // Settings aus API-Response extrahieren
      const newSettings = {
        appName: data.app_name?.value || DEFAULT_SETTINGS.appName,
        logoUrl: data.logo_url?.value || DEFAULT_SETTINGS.logoUrl,
        showSponsor: data.show_sponsor?.value === 'true',
        themeMode: data.theme_mode?.value || DEFAULT_SETTINGS.themeMode,
        slideDuration: parseInt(data.slide_duration?.value) || DEFAULT_SETTINGS.slideDuration,
        sponsorRotationDuration: parseInt(data.sponsor_rotation_duration?.value) || DEFAULT_SETTINGS.sponsorRotationDuration,
        qrUrl: data.qr_url?.value || '',
        sponsors: sponsorsResponse.data || []
      };

      setSettings(newSettings);

      // Browser-Titel aktualisieren
      if (newSettings.appName) {
        document.title = newSettings.appName;
      }

    } catch (err) {
      console.error('Fehler beim Laden der Einstellungen:', err);
      setError('Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  // Settings beim Mount laden
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Funktion zum Aktualisieren der Settings (nach Änderungen in Admin)
  const refreshSettings = useCallback(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Kontext-Wert
  const contextValue = {
    settings,
    loading,
    error,
    refreshSettings
  };

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
}

export default SettingsContext;
